import React, { useEffect, useMemo } from "react";
import { useFetchMaritalStatus, useFetchUser } from "@anefi/services";
import { convertDataToDataSelect } from "@anefi/utils";
import { TextInput } from "artisn-ui-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import Styles from "./StepThreeForm.styles";
import { StepThreeFormProps as Props } from "./StepThreeForm.types";
import { StepThreeFormValues } from "./StepThreeForm.types";
import Select from "components/global/Select/Select";
import useCheckoutForms from "../../hooks/useCheckoutForms";
import useI18n from "hooks/useI18n";
import useAuth from "contexts/auth/auth.context.hooks";

const StepThreeForm: React.FC<Props> = props => {
  const { register, formState, control } =
    useFormContext<StepThreeFormValues>();
  const { data: maritalStatusData } = useFetchMaritalStatus();
  const t = useI18n();
  const auth = useAuth();
  const { data: user } = useFetchUser(auth);
  const { additionalInfo } = user ?? {};
  const { setDocumentCode, setFormType, isFormComplete } = useCheckoutForms();

  const maritalStatusCode = useWatch({ control, name: "maritalStatusCode" });

  const spouseDocumentTypeCode = useWatch({
    control,
    name: "spouseDocumentTypeCode"
  });

  const maritalStatusForSelect = useMemo(
    () => convertDataToDataSelect(maritalStatusData, "code", "name"),
    [maritalStatusData]
  );

  const { maritalStatusCode: maritalStatusCodeError } = formState?.errors ?? {};
  const { spouseDependentTypeCode, spouseName } = formState?.errors ?? {};
  const { spouseDocumentTypeCode: spouseDocumentTypeCodeError } =
    formState?.errors ?? {};
  const { spouseDocument } = formState?.errors ?? {};

  useEffect(() => {
    setFormType(maritalStatusCode);
  }, [maritalStatusCode, setFormType]);

  useEffect(() => {
    setDocumentCode(spouseDocumentTypeCode);
  }, [setDocumentCode, spouseDocumentTypeCode]);

  return (
    <Styles className="StepThreeForm">
      <p className="StepThreeForm__title">
        {isFormComplete ? "Información del conyuge" : "Estado civil actual"}
      </p>
      <Controller
        name="maritalStatusCode"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            className="StepThreeForm__selectCode"
            selectData={maritalStatusForSelect}
            label={t.stepThreeForm.maritalStatus}
            placeholder={t.stepThreeForm.maritalStatus}
            errorMessage={maritalStatusCodeError?.message}
          />
        )}
      />
      {isFormComplete ? (
        <>
          <TextInput
            {...register("spouseDependentTypeCode")}
            className="field"
            label={t.stepThreeForm.dependentType}
            errorMessage={spouseDependentTypeCode?.message}
            disabled
          />
          <TextInput
            {...register("spouseName")}
            className="field"
            label={t.commonFieldForm.fullName}
            placeholder={t.commonFieldForm.fullName}
            errorMessage={spouseName?.message}
          />
          <p className="StepThreeForm__text">
            {t.commonFieldForm.documentType}
          </p>
          <div className="radioContainer">
            <div className="radio">
              <input
                {...register("spouseDocumentTypeCode", { required: true })}
                type="radio"
                value="1"
                id="1"
                defaultChecked={additionalInfo?.spouseDocumentTypeCode === "1"}
              />
              <label htmlFor="site">{t.commonFieldForm.document}</label>
            </div>
            <div className="radio">
              <input
                {...register("spouseDocumentTypeCode", { required: true })}
                type="radio"
                value="2"
                id="2"
                defaultChecked={additionalInfo?.spouseDocumentTypeCode === "2"}
              />
              <label htmlFor="remote">{t.commonFieldForm.ruc}</label>
            </div>
            {spouseDocumentTypeCodeError?.message ? (
              <span id="gender-error" className="errorMessage">
                {spouseDocumentTypeCodeError?.message}
              </span>
            ) : null}
          </div>
          <TextInput
            {...register("spouseDocument")}
            className="field"
            label={t.commonFieldForm.documentLabel}
            placeholder="0000000000"
            errorMessage={spouseDocument?.message}
          />
        </>
      ) : null}
    </Styles>
  );
};

StepThreeForm.defaultProps = {};

export default StepThreeForm;
